import type { TFunction } from "i18next";
import type { RouteObject } from "react-router";
import { Navigate } from "react-router";
import { ACCESS_RULES } from "src/utils/permissionUtils";
import { buildEntitySection } from "./entitySectionRoutes";
import { SECTION_ICONS } from "./icons";
import { lazyRoute } from "./lazyRoutes";
import { URLS } from "./urls";

/** Location routes, mounted under `/configuration` and again under `/setup`. */
export const getLocationRoutes = (
  locationsPath: string = URLS.locations,
): RouteObject[] => [
  buildEntitySection({
    path: locationsPath,
    icon: SECTION_ICONS.locations,
    label: (t: TFunction) => t("common::Locations"),
    list: lazyRoute(() => import("src/views/locations/LocationList")),
    details: lazyRoute(() => import("src/views/locations/LocationDetails")),
    form: lazyRoute(() => import("src/views/locations/LocationForm")),
    entityLabel: () =>
      import("src/views/locations/locationHooks").then(
        (module) => module.useLocationCrumbLabel,
      ),
    access: {
      write: ACCESS_RULES.locationsWrite,
    },
    attributeValues: "location",
    sectionChildren: [
      {
        // Groups live under the locations path, so they inherit its crumb.
        // A group has no page of its own; both routes below redirect to the
        // location list, which is where groups are managed from.
        path: "groups",
        children: [
          { index: true, element: <Navigate to={locationsPath} /> },
          {
            path: "add",
            lazy: lazyRoute(
              () => import("src/views/locations/LocationGroupForm"),
            ),
            handle: {
              access: ACCESS_RULES.locationsWrite,
              crumb: {
                // Matches the button on the location list that leads here.
                label: (t: TFunction) => t("location::Add Group"),
              },
            },
          },
          {
            // No crumb for the group itself: it has no page to link to, and
            // a bare name here would read as a location. The action names it.
            path: ":id",
            children: [
              { index: true, element: <Navigate to={locationsPath} /> },
              {
                path: "edit",
                lazy: lazyRoute(
                  () => import("src/views/locations/LocationGroupForm"),
                ),
                handle: {
                  access: ACCESS_RULES.locationsWrite,
                  // Matches the row action in the location tree.
                  crumb: {
                    label: (t: TFunction) => t("location::Edit Group"),
                  },
                },
              },
            ],
          },
        ],
      },
    ],
  }),
];

export const getReaderRoutes = (
  readersPath: string = URLS.readers,
): RouteObject[] => [
  buildEntitySection({
    path: readersPath,
    icon: SECTION_ICONS.rfidReaders,
    label: (t: TFunction) => t("common::RFID Readers"),
    list: lazyRoute(() => import("src/views/rfidReaders/RfidReaderList")),
    details: lazyRoute(() => import("src/views/rfidReaders/RfidReaderDetails")),
    form: lazyRoute(() => import("src/views/rfidReaders/RfidReaderForm")),
    entityLabel: () =>
      import("src/views/rfidReaders/rfidReaderHooks").then(
        (module) => module.useRfidReaderCrumbLabel,
      ),
    access: {
      read: ACCESS_RULES.readersRead,
      write: ACCESS_RULES.readersWrite,
    },
    sectionChildren: [
      {
        path: "detected",
        lazy: lazyRoute(
          () => import("src/views/rfidReaders/DetectedReaderList"),
        ),
        handle: {
          access: ACCESS_RULES.readersRead,
          crumb: {
            label: (t: TFunction) => t("rfid_reader::Detected Readers"),
          },
        },
      },
    ],
    entityChildren: [
      {
        path: "configuration",
        handle: {
          crumb: {
            label: (t: TFunction) => t("rfid_reader::Configuration Revisions"),
          },
        },
        children: [
          {
            index: true,
            lazy: lazyRoute(
              () => import("src/views/rfidReaders/ReaderConfigRevisionList"),
            ),
            handle: { access: ACCESS_RULES.readersRead },
          },
          {
            path: "edit",
            lazy: lazyRoute(
              () => import("src/views/rfidReaders/ReaderConfigForm"),
            ),
            handle: {
              access: ACCESS_RULES.readersWrite,
              crumb: { label: (t: TFunction) => t("common::Edit") },
            },
          },
        ],
      },
      {
        path: "antennas/:subId",
        element: <Navigate to=".." relative="path" />,
      },
    ],
  }),
];

export const getAttributeDefinitionRoutes = (
  attributeDefinitionsPath: string = URLS.attributeDefinitions,
): RouteObject[] => [
  buildEntitySection({
    path: attributeDefinitionsPath,
    icon: SECTION_ICONS.attributeDefinitions,
    label: (t: TFunction) => t("common::Attribute Definitions"),
    list: lazyRoute(
      () => import("src/views/attributeDefinitions/AttributeDefinitionList"),
    ),
    details: lazyRoute(
      () => import("src/views/attributeDefinitions/AttributeDefinitionDetails"),
    ),
    form: lazyRoute(
      () => import("src/views/attributeDefinitions/AttributeDefinitionForm"),
    ),
    entityLabel: () =>
      import("src/views/attributeDefinitions/attributeDefinitionHooks").then(
        (module) => module.useAttributeDefinitionCrumbLabel,
      ),
    access: {
      read: ACCESS_RULES.dataDefinitionsRead,
      write: ACCESS_RULES.dataDefinitionsWrite,
    },
  }),
];

export const getDataTaskRoutes = (
  dataTasksPath: string = URLS.dataTasks,
): RouteObject[] => [
  buildEntitySection({
    path: dataTasksPath,
    icon: SECTION_ICONS.dataTasks,
    label: (t: TFunction) => t("common::Data Tasks"),
    list: lazyRoute(() => import("src/views/dataTasks/DataTaskList")),
    details: lazyRoute(() => import("src/views/dataTasks/DataTaskDetails")),
    form: lazyRoute(() => import("src/views/dataTasks/DataTaskForm")),
    entityLabel: () =>
      import("src/views/dataTasks/dataTaskHooks").then(
        (module) => module.useDataTaskCrumbLabel,
      ),
    access: { read: ACCESS_RULES.admin, write: ACCESS_RULES.admin },
  }),
];

export const getSensorGroupRoutes = (
  sensorGroupsPath: string = URLS.sensorGroups,
): RouteObject[] => [
  buildEntitySection({
    path: sensorGroupsPath,
    icon: SECTION_ICONS.sensorGroups,
    label: (t: TFunction) => t("common::Sensor Groups"),
    list: lazyRoute(() => import("src/views/groups/SensorGroupList")),
    details: lazyRoute(() => import("src/views/groups/SensorGroupDetails")),
    form: lazyRoute(() => import("src/views/groups/SensorGroupForm")),
    entityLabel: () =>
      import("src/views/groups/sensorGroupHooks").then(
        (module) => module.useSensorGroupCrumbLabel,
      ),
    access: {
      read: ACCESS_RULES.sensorsRead,
      write: ACCESS_RULES.sensorsWrite,
    },
    attributeValues: "group",
  }),
];

export const getLicenceRoutes = (
  licencesPath: string = URLS.licences,
): RouteObject[] => [
  buildEntitySection({
    path: licencesPath,
    icon: SECTION_ICONS.licence,
    label: (t: TFunction) => t("common::Licences"),
    list: lazyRoute(() => import("src/views/licences/LicencesList")),
    details: lazyRoute(() => import("src/views/licences/LicencesDetails")),
    form: lazyRoute(() => import("src/views/licences/LicencesForm")),
    entityLabel: () =>
      import("src/views/licences/licenceHooks").then(
        (module) => module.useLicenceCrumbLabel,
      ),
    access: {
      read: ACCESS_RULES.licencesRead,
      write: ACCESS_RULES.licencesWrite,
    },
  }),
];
